/* eslint-disable react-refresh/only-export-components */
import React, { Suspense, useState, useEffect } from 'react';
import { IconCar, IconParking, IconAlertTriangle } from '@tabler/icons-react';
import { createScopedLogger } from '@/utils/logger.js';
import { lazyWithRetry } from '@/utils/lazyWithRetry.js';

const log = createScopedLogger('ValetService');

// Use lazyWithRetry instead of React.lazy to handle chunk load failures after deployments
const lazy = lazyWithRetry;

/**
 * ValetService - The Complete Metaphor
 * 
 * A valet fetches your vehicle from the parking lot.
 * 
 * - Lots: Business domains where vehicles are parked (assignments lot, payouts lot, etc.)
 * - Vehicles: The actual React components you need
 * - Valet: This service that fetches vehicles on demand
 * - Parking Spot: The specific location (path) of a vehicle in a lot
 * 
 * Usage: <Valet lot="assignments" vehicle="AssignmentCenter" />
 */

// AssignmentCenter is the most-used page in the app and it has exactly ONE loader.
// Every route name below that lands on Assignments points at THIS constant — never its
// own lazy() call. Seven separate lazy() wrappers used to exist for this one file, which
// meant seven independent load paths for the screen the team lives in all day, and only
// some of them carried the stale-chunk retry. One page, one door.
const AssignmentCenterVehicle = lazy(() => import('@/lots/assignments/vehicles/AssignmentCenter'));

// Vehicle Registry - Maps lot.vehicle to actual lazy-loaded components
const vehicleRegistry = {
  // Assignments Lot
  'assignments.AssignmentCenter': AssignmentCenterVehicle,
  'assignments.AssignmentCenterOptimized': AssignmentCenterVehicle, // legacy name from lot config — same screen
  'assignments.WeekSelector': lazy(() => import('@/lots/assignments/vehicles/WeekSelector')),
  'assignments.ClientSelector': lazy(() => import('@/lots/shared/components/ClientSelector')),
  'assignments.AssignmentCard': lazy(() => import('@/lots/assignments/vehicles/AssignmentCard')),
  'assignments.AssignmentGrid': lazy(() => import('@/lots/assignments/vehicles/AssignmentGrid')),
  'assignments.AssignmentsHeader': lazy(() => import('@/lots/assignments/vehicles/AssignmentsHeader')),
  'assignments.Dashboard': AssignmentCenterVehicle, // Main dashboard
  'assignments.index': AssignmentCenterVehicle, // Default
  'assignments.ServiceDashboard': lazy(() => import('@/lots/assignments/vehicles/ServiceDashboard')), // Service fulfillment
  'assignments.OpenAssignments': lazy(() => import('@/lots/assignments/vehicles/OpenAssignments')), // Platform-wide open assignments
  'assignments.ValetAssignments': lazy(() => import('@/lots/assignments/vehicles/ValetAssignmentBrowser')),
  'assignments.ValetAssignmentList': lazy(() => import('@/lots/assignments/vehicles/ValetAssignmentBrowser')),
  'assignments.ValetAssignmentListOptimal': lazy(() => import('@/lots/assignments/vehicles/ValetAssignmentBrowser')),

  // Court Vision Lot (/intel/court-vision — list-first fulfillment audit).
  // These entries were MISSING, so the route errored "Vehicle not found" (task #63).
  'court-vision.CourtVisionSimple': lazy(() => import('@/lots/agency/vehicles/court-vision/CourtVisionSimple')),
  'court-vision.index': lazy(() => import('@/lots/agency/vehicles/court-vision/CourtVisionSimple')),

  // Open Assignments Lot (separate routing)
  'open-assignments.OpenAssignments': lazy(() => import('@/lots/assignments/vehicles/OpenAssignments')),
  'open-assignments.index': lazy(() => import('@/lots/assignments/vehicles/OpenAssignments')),
  'operations.open-assignments': lazy(() => import('@/lots/assignments/vehicles/OpenAssignments')),
  'operations.OpenAssignments': lazy(() => import('@/lots/assignments/vehicles/OpenAssignments')),

  // Clients Lot
  'clients.index': lazy(() => import('@/lots/clients/index')), // Default
  'clients.Clients': lazy(() => import('@/lots/clients/index')),
  'clients.ClientDirectory': lazy(() => import('@/lots/clients/components/ClientList')),
  'clients.ClientList': lazy(() => import('@/lots/clients/components/ClientList')),
  'clients.ClientCard': lazy(() => import('@/lots/clients/components/ClientCard')),
  'clients.ClientFilters': lazy(() => import('@/lots/clients/components/ClientFilters')),

  // Equipment Lot
  'equipment.EquipmentCenter': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')),
  'equipment.EquipmentRequestCenter': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')), // Alias for GARAGES config
  'equipment.Dashboard': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')), // Main dashboard
  'equipment.index': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')), // Default

  // Valets Lot
  'valets.ValetManager': lazy(() => import('@/lots/valets/vehicles/ValetManager')),
  'valets.ValetWhereabouts': lazy(() => import('@/lots/valets/vehicles/ValetWhereabouts')),
  'valets.ReliabilityTab': lazy(() => import('@/lots/valets/vehicles/ReliabilityTab')),
  'valets.index': lazy(() => import('@/lots/valets/vehicles/ValetManager')),

  // HR Valets Lot (same as valets but for HR garage)
  'hr.valets': lazy(() => import('@/lots/valets/vehicles/ValetManager')),
  'hr.ValetManager': lazy(() => import('@/lots/valets/vehicles/ValetManager')),
  'hr.ReliabilityTab': lazy(() => import('@/lots/valets/vehicles/ReliabilityTab')),
  'hr.index': lazy(() => import('@/lots/valets/vehicles/ValetManager')),

  // HR Valet Onboarding Lot
  'hr.valet-onboarding': lazy(() => import('@/lots/valet-mobile/vehicles/ValetMigrationMonitor')),
  'hr.ValetMigrationMonitor': lazy(() => import('@/lots/valet-mobile/vehicles/ValetMigrationMonitor')),
  'valet-onboarding.index': lazy(() => import('@/lots/valet-mobile/vehicles/ValetMigrationMonitor')),
  'valet-onboarding.ValetMigrationMonitor': lazy(() => import('@/lots/valet-mobile/vehicles/ValetMigrationMonitor')),

  // Badges Lot
  'badges.BadgesLot': lazy(() => import('@/lots/badges/vehicles/BadgesLot')),
  // /valet/badges — garages.js named `ValetBadges` but no such component existed,
  // so the tab rendered "Vehicle Not Available" for every valet-certified user.
  'badges.ValetBadges': lazy(() => import('@/lots/badges/vehicles/ValetBadges')),
  'badges.BadgeManagementDashboard': lazy(() => import('@/lots/badges/vehicles/BadgeManagementDashboard')),
  'badges.index': lazy(() => import('@/lots/badges/vehicles/BadgesLot')),

  // HR Badges Lot (same as badges but for HR garage)
  'hr.badges': lazy(() => import('@/lots/badges/vehicles/BadgesLot')),
  'hr.BadgesLot': lazy(() => import('@/lots/badges/vehicles/BadgesLot')),
  'hr.BadgeManagementDashboard': lazy(() => import('@/lots/badges/vehicles/BadgeManagementDashboard')),

  // HR Licenses — every driver's license, hr-licenses badge only (Sean 2026-10-01)
  'licenses.LicensesGallery': lazy(() => import('@/lots/recruits/vehicles/LicensesGallery')),

  // HR Ratings Lot
  'hr.ratings': lazy(() => import('@/components/ratings/HRRatingsTable')),
  'hr.HRRatingsTable': lazy(() => import('@/components/ratings/HRRatingsTable')),
  'ratings.HRRatingsTable': lazy(() => import('@/components/ratings/HRRatingsTable')),
  'ratings.ratings': lazy(() => import('@/components/ratings/HRRatingsTable')),
  'ratings.index': lazy(() => import('@/components/ratings/HRRatingsTable')),

  // HR Recruits Lot - Parkology University Certification Pipeline
  'hr.recruits': lazy(() => import('@/lots/recruits/vehicles/RecruitmentDashboard')),
  'hr.RecruitingLot': lazy(() => import('@/lots/recruits/vehicles/RecruitmentDashboard')),
  'recruits.recruits': lazy(() => import('@/lots/recruits/vehicles/RecruitmentDashboard')),
  'recruits.RecruitingLot': lazy(() => import('@/lots/recruits/vehicles/RecruitmentDashboard')),
  'recruits.index': lazy(() => import('@/lots/recruits/vehicles/RecruitmentDashboard')),
  // HR Paperwork Check — veteran cohort missing MVR/Stripe/W-9/phone
  'hr.PaperCheckDashboard': lazy(() => import('@/lots/recruits/vehicles/PaperCheckDashboard')),
  'recruits.PaperCheckDashboard': lazy(() => import('@/lots/recruits/vehicles/PaperCheckDashboard')),
  // (Retired 7/12) The unrouted `components/recruitment/*` legacy brick + its
  // 3 keys were dead twins of the live `lots/recruits` board — deleted, not routed.

  // Parkology - Valet Development Center (Dashboard) & Academy (Lessons)
  'parkology.index': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  'parkology.ParkologyDashboard': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  'parkology.ParkologyAcademy': lazy(() => import('@/lots/parkology/vehicles/ParkologyAcademy')),
  'parkology.academy': lazy(() => import('@/lots/parkology/vehicles/ParkologyAcademy')),
  'hr.parkology': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  'hr.ParkologyDashboard': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  'hr.ParkologyAcademy': lazy(() => import('@/lots/parkology/vehicles/ParkologyAcademy')),
  'training.parkology': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  // School of Parkology aliases (from garages.js valet garage)
  'school.index': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  'school.SchoolOfParkology': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),
  'valet.school': lazy(() => import('@/lots/parkology/vehicles/ParkologyDashboard')),

  // Claims Lot
  'claims.ClaimsTable': lazy(() => import('@/lots/claims/vehicles/ClaimsTable')),
  'claims.Claims': lazy(() => import('@/lots/claims/vehicles/claims')),
  'claims.ClaimsCenter': lazy(() => import('@/lots/claims/vehicles/claims')), // Main component for GARAGES config - Full dashboard with Kanban + Table views
  // ClaimsPipelineDashboard (a 3rd light-theme pipeline riding EventsDashboard) deleted
  // 2026-08-12 — every claims route lands on the one canonical lot (claims.jsx).
  'claims.ClaimsPipeline': lazy(() => import('@/lots/claims/vehicles/claims')),
  'claims.Pipeline': lazy(() => import('@/lots/claims/vehicles/claims')),
  'claims.CommandCenter': lazy(() => import('@/lots/claims/vehicles/claims')),

  'claims.index': lazy(() => import('@/lots/claims')), // Points to index.js which exports claims.jsx as default

  // Incidents Lot - Incident Reports Dashboard
  'incidents.IncidentReportsCenter': lazy(() => import('@/lots/incidents/vehicles/incidentReports')), // Main component for GARAGES config
  'incidents.IncidentReportsDashboard': lazy(() => import('@/lots/incidents/vehicles/incidentReports')),
  'incidents.IncidentReportsTable': lazy(() => import('@/lots/incidents/vehicles/IncidentReportsTable')),
  'incidents.index': lazy(() => import('@/lots/incidents')), // Points to index.js which exports incidentReports.jsx as default

  // Finance Lot - Consolidated Components (Nov 2025)
  'finance.FinanceHub': lazy(() => import('@/lots/finance/vehicles/FinanceHub')),
  'finance.FinanceDashboardEnhanced': lazy(() => import('@/lots/finance/vehicles/FinanceDashboardEnhanced')),
  'finance.InvoiceDashboard': lazy(() => import('@/lots/finance/vehicles/InvoiceDashboard')),
  'finance.InvoiceBuilder': lazy(() => import('@/lots/finance/vehicles/InvoiceBuilder')),
  'finance.CollectionsDashboard': lazy(() => import('@/lots/finance/vehicles/CollectionsDashboard')),
  'finance.TaxTrackingDashboard': lazy(() => import('@/lots/finance/vehicles/TaxTrackingDashboard')),
  'finance.FinanceGarage': lazy(() => import('@/lots/finance/BookkeepingLot')), // Backward compatibility alias
  'finance.BookkeepingLot': lazy(() => import('@/lots/finance/BookkeepingLot')),
  'finance.ReportsLot': lazy(() => import('@/lots/finance/BookkeepingLot')), // Backward compatibility alias
  'finance.BreakingBread': lazy(() => import('@/lots/finance/vehicles/breaking-bread')),
  'finance.index': lazy(() => import('@/lots/finance/vehicles/FinanceDashboardEnhanced')), // Default to enhanced dashboard

  // Finance Dashboard Aliases (by lotId from garages.js)
  'dashboard.FinanceDashboardEnhanced': lazy(() => import('@/lots/finance/vehicles/FinanceDashboardEnhanced')),

  // Collections Lot
  'collections.CollectionsDashboard': lazy(() => import('@/lots/finance/vehicles/CollectionsDashboard')),
  'collections.index': lazy(() => import('@/lots/finance/vehicles/CollectionsDashboard')),

  // Invoices Lot (consolidated invoice management)
  'invoices.index': lazy(() => import('@/lots/finance/vehicles/InvoiceDashboard')),
  'invoices.InvoicesCenter': lazy(() => import('@/lots/finance/vehicles/InvoiceDashboard')), // Main component for GARAGES config
  'invoices.InvoiceDashboard': lazy(() => import('@/lots/finance/vehicles/InvoiceDashboard')),
  'invoices.GroupBillingDashboard': lazy(() => import('@/lots/finance/vehicles/GroupBillingDashboard')),
  'invoices.InvoiceBuilder': lazy(() => import('@/lots/finance/vehicles/InvoiceBuilder')),

  // Bookkeeping Lot
  'bookkeeping.BookkeepingLot': lazy(() => import('@/lots/finance/BookkeepingLot')),
  'bookkeeping.index': lazy(() => import('@/lots/finance/BookkeepingLot')),

  // Reports Lot
  'reports.ReportsLot': lazy(() => import('@/lots/finance/BookkeepingLot')), // Backward compatibility alias
  'reports.index': lazy(() => import('@/lots/finance/BookkeepingLot')), // Backward compatibility alias

  // Tax Tracking Lot
  'tax-tracking.TaxTrackingDashboard': lazy(() => import('@/lots/finance/vehicles/TaxTrackingDashboard')),
  'tax-tracking.index': lazy(() => import('@/lots/finance/vehicles/TaxTrackingDashboard')),

  // Orchard Lot (Performance Leaderboard)

  // Breaking Bread Lot (Fund Allocation)
  'breaking-bread.BreakingBread': lazy(() => import('@/lots/finance/vehicles/breaking-bread')),
  'breaking-bread.index': lazy(() => import('@/lots/finance/vehicles/breaking-bread')),

  // Partners Lot (operator-contractor partnerships — 4u, future Tim Moore, etc.)
  'partners.PartnersLot': lazy(() => import('@/lots/partners/PartnersLot')),
  'partners.index': lazy(() => import('@/lots/partners/PartnersLot')),
  'index.PartnersLot': lazy(() => import('@/lots/partners/PartnersLot')),
  '4u.PartnersLot': lazy(() => import('@/lots/partners/PartnersLot')),
  '4u.PartnerDashboard': lazy(() => import('@/lots/partners/PartnerDashboard')),

  // Geoffrey's legacy finance board remains a review-only compatibility route

  // Disbursements Lot
  'disbursements.DisbursementsLot': lazy(() => import('@/lots/disbursements/vehicles/DisbursementsLot')), // New credit/debit deck UI
  'disbursements.DisbursementsCenter': lazy(() => import('@/lots/disbursements/vehicles/DisbursementsLot')),
  'disbursements.DisbursementsDashboard': lazy(() => import('@/lots/disbursements/vehicles/DisbursementsDashboard')), // Legacy
  'disbursements.DisbursementsWithTabs': lazy(() => import('@/lots/disbursements/vehicles/DisbursementsWithTabs')), // Tabbed interface with Pay Issues
  'disbursements.PayIssuesTab': lazy(() => import('@/lots/disbursements/vehicles/PayIssuesTab')), // Pay Issues admin queue
  'payroll.index': lazy(() => import('@/lots/disbursements/vehicles/index')),
  'payroll.PayrollCenter': lazy(() => import('@/lots/disbursements/vehicles/DisbursementsLot')),

  // Accounting Hub Lot (AI Accounting System)
  'accounting-hub.FinanceGarage': lazy(() => import('@/lots/finance/BookkeepingLot')), // Backward compatibility alias
  'accounting-hub.index': lazy(() => import('@/lots/finance/BookkeepingLot')), // Backward compatibility alias

  // Clients Lot (Client Finance)
  'clients.ClientManagement': lazy(() => import('@/lots/clients/index')),

  // Operations Lot
  'operations.assignments': AssignmentCenterVehicle,
  'operations.AssignmentCenter': AssignmentCenterVehicle,
  'operations.clients': lazy(() => import('@/lots/clients/index')),
  'operations.Clients': lazy(() => import('@/lots/clients/index')),
  'operations.ClientManagement': lazy(() => import('@/lots/clients/index')),
  'operations.events': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'operations.Events': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'operations.EventsDashboardMain': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'operations.products': lazy(() => import('@/lots/products/vehicles/ProductTypesManager')), // Uses BricksOS
  'operations.Products': lazy(() => import('@/lots/products/vehicles/ProductTypesManager')), // Uses BricksOS
  'operations.equipment': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')),
  'operations.Equipment': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')),
  'operations.EquipmentRequestCenter': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')),
  'operations.shuttle': lazy(() => import('@/lots/shuttle/vehicles/FleetManager')),
  'operations.Shuttle': lazy(() => import('@/lots/shuttle/vehicles/FleetManager')),
  'operations.FleetManager': lazy(() => import('@/lots/shuttle/vehicles/FleetManager')),
  'operations.claims': lazy(() => import('@/lots/claims/vehicles/claims')),
  'operations.Claims': lazy(() => import('@/lots/claims/vehicles/claims')),
  'operations.ClaimsCenter': lazy(() => import('@/lots/claims/vehicles/claims')), // one canonical claims lot
  'operations.locations': lazy(() => import('@/lots/locations/vehicles/LocationManager')),
  'operations.Locations': lazy(() => import('@/lots/locations/vehicles/LocationManager')),
  'operations.LocationManager': lazy(() => import('@/lots/locations/vehicles/LocationManager')),
  'operations.index': AssignmentCenterVehicle, // Default to AssignmentCenter

  // Pay Change Requests Lot
  'pay-change-requests.PayChangeRequests': lazy(() => import('@/lots/operations/vehicles/PayChangeRequests')),
  'pay-change-requests.index': lazy(() => import('@/lots/operations/vehicles/PayChangeRequests')),
  'operations.pay-change-requests': lazy(() => import('@/lots/operations/vehicles/PayChangeRequests')),
  'operations.PayChangeRequests': lazy(() => import('@/lots/operations/vehicles/PayChangeRequests')),

  // GSeater Lot - Operations Dashboard for ticket queue management
  'garage.GSeaterDashboard': lazy(() => import('@/lots/garage/GSeaterDashboard')),
  'gseater.GSeaterDashboard': lazy(() => import('@/lots/garage/GSeaterDashboard')),
  'operations.gseater': lazy(() => import('@/lots/garage/GSeaterDashboard')),
  'operations.GSeaterDashboard': lazy(() => import('@/lots/garage/GSeaterDashboard')),

  // Locations Lot
  'locations.index': lazy(() => import('@/lots/locations/vehicles/LocationManager')),
  'locations.LocationManager': lazy(() => import('@/lots/locations/vehicles/LocationManager')),

  // Events Lot
  'events.index': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  // Compatibility aliases converge on the one active Events surface. The old
  // dashboard implementations remain in the repository for removal review but
  // are no longer reachable through dynamic lot routing.
  'events.EventsDashboard': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'events.EventsDashboardDark': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'events.EventsKanbanBoard': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'events.EventsDashboardMain': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'events.EventManager': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),
  'events.EventBook': lazy(() => import('@/lots/events/vehicles/EventsDashboardMain')),

  // Products Lot
  'products.index': lazy(() => import('@/lots/products/vehicles/ProductsSimpleTable')), // QBO linking visible
  'products.products': lazy(() => import('@/lots/products/vehicles/ProductsSimpleTable')), // Main products table with QBO status
  'products.ProductsTable': lazy(() => import('@/lots/products/vehicles/ProductsTable')),
  'products.ProductsSimpleTable': lazy(() => import('@/lots/products/vehicles/ProductsSimpleTable')),
  'products.ProductTypesManager': lazy(() => import('@/lots/products/vehicles/ProductTypesManager')),

  // Shuttle Lot
  'shuttle.index': lazy(() => import('@/lots/shuttle/vehicles/index')),
  'shuttle.ShuttleIndex': lazy(() => import('@/lots/shuttle/vehicles/index')),
  'shuttle.BookingPipeline': lazy(() => import('@/lots/shuttle/vehicles/BookingPipeline')),
  'shuttle.FleetManager': lazy(() => import('@/lots/shuttle/vehicles/FleetManager')),
  'shuttle.PublicShuttleBooking': lazy(() => import('@/lots/shuttle/vehicles/PublicShuttleBooking')),
  'shuttle.BookingForm': lazy(() => import('@/lots/shuttle/vehicles/PublicShuttleBooking')), // Alias

  // Admin Lot
  'admin.index': lazy(() => import('@/lots/admin/vehicles/index')),
  'admin.dashboard': lazy(() => import('@/lots/admin/vehicles/index')),


  // Shuttle booking interface (ShuttleClientPortal) — NOT a client portal viewer
  'shuttle.ClientPortal': lazy(() => import('@/lots/shuttle/vehicles/ClientPortal')),
  'shuttle.ShuttleClientPortal': lazy(() => import('@/lots/shuttle/vehicles/ClientPortal')),

  // CRM Lot - B2B Sales Pipeline (consolidated from lead-engine + crm)
  'crm.index': lazy(() => import('@/lots/crm/index')),
  'crm.CRM': lazy(() => import('@/lots/crm/index')),
  'crm.PipelineKanban': lazy(() => import('@/lots/crm/vehicles/PipelineKanban')),
  'crm.ContactsView': lazy(() => import('@/lots/crm/vehicles/ContactsView')),
  'crm.SequenceEditor': lazy(() => import('@/lots/crm/vehicles/SequenceEditor')),
  'crm.SettingsView': lazy(() => import('@/lots/crm/vehicles/SettingsView')),
  'crm.Masterlist': lazy(() => import('@/lots/marketing/vehicles/Masterlist')),

  // Proposals Lot - AI-Powered Proposal Engine
  'proposals.index': lazy(() => import('@/lots/proposals/vehicles/ProposalList')),
  'proposals.ProposalList': lazy(() => import('@/lots/proposals/vehicles/ProposalList')),
  'proposals.list': lazy(() => import('@/lots/proposals/vehicles/ProposalList')),
  'proposals.proposals': lazy(() => import('@/lots/proposals/vehicles/ProposalList')), // /branding/proposals — garages.js lot.component literal 'proposals'

  'proposals.ProposalDetail': lazy(() => import('@/lots/proposals/vehicles/ProposalDetail')),
  'proposals.detail': lazy(() => import('@/lots/proposals/vehicles/ProposalDetail')),
  'branding.proposals': lazy(() => import('@/lots/proposals/vehicles/ProposalList')),

  // Branding Lot
  'branding.index': lazy(() => import('@/lots/branding/vehicles/ApparelStore')),
  'branding.apparel': lazy(() => import('@/lots/branding/vehicles/ApparelStore')),
  'branding.ApparelStore': lazy(() => import('@/lots/branding/vehicles/ApparelStore')),
  'branding.crm': lazy(() => import('@/lots/crm/index')),

  // Legacy lead-engine aliases → CRM
  'lead-engine.index': lazy(() => import('@/lots/crm/index')),
  'lead-engine.LeadEngine': lazy(() => import('@/lots/crm/index')),

  // Marketing Lot (Masterlist + CRM)
  'marketing.index': lazy(() => import('@/lots/marketing/vehicles/Masterlist')),
  'marketing.Masterlist': lazy(() => import('@/lots/marketing/vehicles/Masterlist')),
  'marketing.masterlist': lazy(() => import('@/lots/marketing/vehicles/Masterlist')),
  'marketing.crm': lazy(() => import('@/lots/crm/index')),
  'masterlist.index': lazy(() => import('@/lots/marketing/vehicles/Masterlist')),
  'masterlist.Masterlist': lazy(() => import('@/lots/marketing/vehicles/Masterlist')),

  // Apparel Lot (routes using lotId='apparel' within branding garage)
  'apparel.ApparelStore': lazy(() => import('@/lots/branding/vehicles/ApparelStore')),
  'apparel.index': lazy(() => import('@/lots/branding/vehicles/ApparelStore')),
  // /branding/apparel — council/KOT admin: Orders · Inventory · Deductions
  'apparel.ApparelAdmin': lazy(() => import('@/lots/odv-apparel/vehicles/ApparelAdmin')),
  'branding.ApparelAdmin': lazy(() => import('@/lots/odv-apparel/vehicles/ApparelAdmin')),

  // Comms Lot (merged: chat + mailroom + discord + unified timeline)
  'comms.index': lazy(() => import('@/lots/comms/vehicles/CommsDashboard')),
  'comms.CommsDashboard': lazy(() => import('@/lots/comms/vehicles/CommsDashboard')),
  'comms-center.index': lazy(() => import('@/lots/comms/vehicles/CommsDashboard')),
  'comms-center.CommsDashboard': lazy(() => import('@/lots/comms/vehicles/CommsDashboard')),
  'comms.chat': lazy(() => import('@/lots/comms/chat/vehicles/ChatDashboard')),
  'comms.ChatDashboard': lazy(() => import('@/lots/comms/chat/vehicles/ChatDashboard')),
  'comms.discord': lazy(() => import('@/lots/comms/discord/DiscordDashboard')),
  'comms.DiscordDashboard': lazy(() => import('@/lots/comms/discord/DiscordDashboard')),
  'comms.mailroom': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),
  'comms.Mailroom': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),
  'comms.sms': lazy(() => import('@/lots/comms/sms/vehicles/SMSCenter')),
  'comms.SMSCenter': lazy(() => import('@/lots/comms/sms/vehicles/SMSCenter')),
  'sms.SMSCenter': lazy(() => import('@/lots/comms/sms/vehicles/SMSCenter')),
  'sms.index': lazy(() => import('@/lots/comms/sms/vehicles/SMSCenter')),
  'index.SMSCenter': lazy(() => import('@/lots/comms/sms/vehicles/SMSCenter')),

  // Phone Lot (call dashboard + analytics + transcripts + extensions)
  'phone.index': lazy(() => import('@/lots/phone')),
  'phone.PhoneLot': lazy(() => import('@/lots/phone')),
  'phone.PhoneDashboard': lazy(() => import('@/lots/phone/vehicles/PhoneDashboard')),
  'phone.CallAnalyticsDashboard': lazy(() => import('@/lots/phone/vehicles/CallAnalyticsDashboard')),
  'phone.ExtensionManager': lazy(() => import('@/lots/phone/vehicles/ExtensionManager')),

  // Quality Control Lot (pop-in inspections + location health)
  'qc.QCDashboard': lazy(() => import('@/lots/qc/vehicles/QCDashboard')), // /qc — routes.js sets vehicleName explicitly

  // Payouts Lot (to be built)
  // 'payouts.PayoutDashboard': lazy(() => import('../../../lotsOS/payouts/vehicles/PayoutDashboard')),
  // 'payouts.CashoutWidget': lazy(() => import('../../../lotsOS/payouts/vehicles/CashoutWidget')),
  
  // Contracts Lot (to be built)
  // 'contracts.ContractManager': lazy(() => import('../../../lotsOS/contracts/vehicles/ContractManager')),
  // 'contracts.RateCalculator': lazy(() => import('../../../lotsOS/contracts/vehicles/RateCalculator')),
  
  // Products Lot (to be built)
  // 'products.ProductEditor': lazy(() => import('../../../lotsOS/products/vehicles/ProductEditor')),
  // 'products.RateMatrix': lazy(() => import('../../../lotsOS/products/vehicles/RateMatrix')),
  
  // Badges Lot (to be built)
  // 'badges.BadgeManager': lazy(() => import('../../../lotsOS/badges/vehicles/BadgeManager')),
  // 'badges.CertificationTracker': lazy(() => import('../../../lotsOS/badges/vehicles/CertificationTracker')),
  
  // Valets Lot (to be built)
  // 'valets.ValetProfile': lazy(() => import('../../../lotsOS/valets/vehicles/ValetProfile')),
  // 'valets.PowerScoreCard': lazy(() => import('../../../lotsOS/valets/vehicles/PowerScoreCard')),
  
  // Clients Lot (to be built)
  // 'clients.ClientDashboard': lazy(() => import('../../../lotsOS/clients/vehicles/ClientDashboard')),
  // 'clients.LocationManager': lazy(() => import('../../../lotsOS/clients/vehicles/LocationManager')),
  
  // Onboarding Lot (to be built)
  // 'onboarding.WelcomeFlow': lazy(() => import('../../../lotsOS/onboarding/vehicles/WelcomeFlow')),
  // 'onboarding.CertificationFlow': lazy(() => import('../../../lotsOS/onboarding/vehicles/CertificationFlow')),
  
  // Ratings Lot (to be built)
  // 'ratings.RatingsDashboard': lazy(() => import('../../../lotsOS/ratings/vehicles/RatingsDashboard')),
  // 'ratings.FeedbackWidget': lazy(() => import('../../../lotsOS/ratings/vehicles/FeedbackWidget')),
  
  // Invoices Lot
  'invoices.invoices': lazy(() => import('@/lots/finance/vehicles/invoices')),

  // Admin Lot
  'admin.ActivityLogs': lazy(() => import('@/lots/admin/vehicles/ActivityLogs')),
  'admin.activity-logs': lazy(() => import('@/lots/admin/vehicles/ActivityLogs')),
  'activity-logs.ActivityLogs': lazy(() => import('@/lots/admin/vehicles/ActivityLogs')),
  'activity-logs.activity-logs': lazy(() => import('@/lots/admin/vehicles/ActivityLogs')),
  
  // Client Portal Admin — token management, access logs, email history
  'admin.ClientPortalAdminDashboard': lazy(() => import('@/lots/client-portal/admin/ClientPortalAdminDashboard')),
  'admin.client-portal': lazy(() => import('@/lots/client-portal/admin/ClientPortalAdminDashboard')),
  'client-portal.ClientPortalAdminDashboard': lazy(() => import('@/lots/client-portal/admin/ClientPortalAdminDashboard')),
  'client-portal.index': lazy(() => import('@/lots/client-portal/admin/ClientPortalAdminDashboard')),
  'client-portal.client-portal': lazy(() => import('@/lots/client-portal/admin/ClientPortalAdminDashboard')),

  'admin.MeetingsLot': lazy(() => import('@/lots/admin/vehicles/MeetingsLot')),
  'admin.meetings': lazy(() => import('@/lots/admin/vehicles/MeetingsLot')),
  'meetings.MeetingsLot': lazy(() => import('@/lots/admin/vehicles/MeetingsLot')),
  'meetings.index': lazy(() => import('@/lots/admin/vehicles/MeetingsLot')),
  
  // Client Portal Viewer — canonical ops-side view: pick any client, see their portal modules
  // All old keys (clients.portal, portal.ClientPortalWrapper, portal-viewer.*, clients.ClientPortalViewer)
  // are consolidated here. ClientPortalWrapper (filtered AssignmentCenter) is retired in favour of
  // ClientPortalViewer which has the full tab set + staff-impersonate link.
  'clients.ClientPortalViewer': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalViewer')),
  'clients.portal': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalViewer')),
  'portal.ClientPortalViewer': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalViewer')),
  'portal.ClientPortalWrapper': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalViewer')),
  'portal-viewer.ClientPortalWrapper': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalViewer')),
  'portal-viewer.ClientPortalViewer': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalViewer')),
  
  'admin.Contracts': lazy(() => import('@/lots/admin/vehicles/Contracts')),
  'admin.contracts': lazy(() => import('@/lots/admin/vehicles/Contracts')),
  'contracts.Contracts': lazy(() => import('@/lots/admin/vehicles/Contracts')),
  'contracts.index': lazy(() => import('@/lots/admin/vehicles/Contracts')),
  'contracts.contracts': lazy(() => import('@/lots/admin/vehicles/Contracts')),
  'contracts.ContractStatusDashboard': lazy(() => import('@/lots/contracts/vehicles/ContractStatusDashboard')),
  'contracts.status': lazy(() => import('@/lots/contracts/vehicles/ContractStatusDashboard')),

  'admin.PlatformManager': lazy(() => import('@/lots/admin/vehicles/PlatformManager')),
  'admin.platform': lazy(() => import('@/lots/admin/vehicles/PlatformManager')),
  'platform.PlatformManager': lazy(() => import('@/lots/admin/vehicles/PlatformManager')),
  'platform.index': lazy(() => import('@/lots/admin/vehicles/PlatformManager')),
  'platform.platform': lazy(() => import('@/lots/admin/vehicles/PlatformManager')),
  
  'admin.Policies': lazy(() => import('@/lots/admin/vehicles/Policies')),
  'admin.policies': lazy(() => import('@/lots/admin/vehicles/Policies')),
  'policies.Policies': lazy(() => import('@/lots/admin/vehicles/Policies')),
  'policies.index': lazy(() => import('@/lots/admin/vehicles/Policies')),
  'policies.policies': lazy(() => import('@/lots/admin/vehicles/Policies')),
  
  'admin.Users': lazy(() => import('@/lots/admin/vehicles/Users')),
  'admin.users': lazy(() => import('@/lots/admin/vehicles/Users')),
  'users.Users': lazy(() => import('@/lots/admin/vehicles/Users')),
  'users.index': lazy(() => import('@/lots/admin/vehicles/Users')),

  // Leadership Management Lot
  'admin.LeadershipManagement': lazy(() => import('@/lots/clients/vehicles/LeadershipManagement')),
  'admin.leadership': lazy(() => import('@/lots/clients/vehicles/LeadershipManagement')),
  'leadership.LeadershipManagement': lazy(() => import('@/lots/clients/vehicles/LeadershipManagement')),
  'leadership.leadership': lazy(() => import('@/lots/clients/vehicles/LeadershipManagement')),
  'leadership.index': lazy(() => import('@/lots/clients/vehicles/LeadershipManagement')),
  'users.users': lazy(() => import('@/lots/admin/vehicles/Users')),

  // Round Table - Operations weekly account health & leader effectiveness
  'round-table.RoundTable': lazy(() => import('@/lots/operations/vehicles/RoundTable')),
  'round-table.index': lazy(() => import('@/lots/operations/vehicles/RoundTable')),
  'operations.RoundTable': lazy(() => import('@/lots/operations/vehicles/RoundTable')),
  'operations.round-table': lazy(() => import('@/lots/operations/vehicles/RoundTable')),

  // Mailroom — standalone lot, three-panel AI email command center
  'mailroom.Mailroom': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),
  'mailroom.index': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),
  'index.Mailroom': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),
  'admin.MailroomLot': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),
  'admin.mailroom': lazy(() => import('@/lots/comms/mailroom/vehicles/Mailroom')),

  // Agents garage — single lot with tabs for proposals, learnings, reports
  'index.AgentCouncilDashboard': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),
  'agents.AgentCouncilDashboard': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),
  // /agents — the Agents garage names its lot `index` (garages.js), so routes.js
  // resolves `index.AgentCommandCenter`. That key was never registered, so the route
  // rendered "Vehicle Not Available" even though the component existed all along.
  'index.AgentCommandCenter': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),
  // Legacy paths
  'admin.CommandCenter': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),
  'admin.command-center': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),
  'command-center.CommandCenter': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),
  'command-center.index': lazy(() => import('@/lots/agency/components/AgentCommandCenter')),

  // Task Bucket
  'tasks.TaskBucket': lazy(() => import('@/lots/tasks/vehicles/TaskBucket')),
  'tasks.index': lazy(() => import('@/lots/tasks/vehicles/TaskBucket')),
  'task-bucket.TaskBucket': lazy(() => import('@/lots/tasks/vehicles/TaskBucket')),
  'task-bucket.index': lazy(() => import('@/lots/tasks/vehicles/TaskBucket')),

  // Client Portal Garage (active components only — portal uses PortalShell routes, not ParkingLot)
  'client-portal.ClientPortalRoster': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalRoster')),
  'client-portal.roster': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalRoster')),
  'client-portal.ClientPortalMessages': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalMessages')),
  'client-portal.messages': lazy(() => import('@/lots/client-portal/vehicles/ClientPortalMessages')),

  // Valet Garage (for badged valets viewing dashboard)
  'dashboard.ValetDashboard': lazy(() => import('@/pages/valet/ValetDashboard')), // /valet
  'wallet.ValetWallet': lazy(() => import('@/lots/valet-mobile/vehicles/ValetWallet')), // /valet/wallet
  'leaderboard.ValetLeaderboard': lazy(() => import('@/lots/gamification/vehicles/ValetLeaderboard')),
  'leaderboard.index': lazy(() => import('@/lots/gamification/vehicles/ValetLeaderboard')),
  'valet.leaderboard': lazy(() => import('@/lots/gamification/vehicles/ValetLeaderboard')),
  'valet.ValetLeaderboard': lazy(() => import('@/lots/gamification/vehicles/ValetLeaderboard')),
  // /valet/equipment merges into the SAME target as Operations→Equipment (equipment.EquipmentRequestCenter below) — one shared surface, not a second one
  'equipment.ValetEquipmentRequests': lazy(() => import('@/lots/equipment/vehicles/EquipmentCenter')),
  'gamification.ValetLeaderboard': lazy(() => import('@/lots/gamification/vehicles/ValetLeaderboard')),
  'gamification.index': lazy(() => import('@/lots/gamification/vehicles/ValetLeaderboard')),

  // Knowledge Base Lot
  'knowledge-base.KnowledgeBaseDashboard': lazy(() => import('@/lots/knowledge-base/vehicles/KnowledgeBaseDashboard')),
  'knowledge-base.browse': lazy(() => import('@/lots/knowledge-base/vehicles/KnowledgeBaseDashboard')),
  'knowledge-base.suggestions': lazy(() => import('@/lots/knowledge-base/vehicles/KnowledgeBaseDashboard')),
  'knowledge-base.index': lazy(() => import('@/lots/knowledge-base/vehicles/KnowledgeBaseDashboard')),
  'browse.KnowledgeBaseDashboard': lazy(() => import('@/lots/knowledge-base/vehicles/KnowledgeBaseDashboard')),
  'suggestions.KnowledgeBaseDashboard': lazy(() => import('@/lots/knowledge-base/vehicles/KnowledgeBaseDashboard')),

  // ODV Matrix — platform event observation terminal (lives in wisdom-capture)
  'wisdom-capture.ODVMatrix': lazy(() => import('@/lots/wisdom-capture/vehicles/ODVMatrix')),
  'matrix.ODVMatrix': lazy(() => import('@/lots/wisdom-capture/vehicles/ODVMatrix')),
  'matrix.index': lazy(() => import('@/lots/wisdom-capture/vehicles/ODVMatrix')),
  'admin.ODVMatrix': lazy(() => import('@/lots/wisdom-capture/vehicles/ODVMatrix')),

  // Voice Analytics — PTT cascade observability dashboard
  'voice-analytics.VoiceAnalyticsDashboard': lazy(() => import('@/lots/voice-analytics/VoiceAnalyticsDashboard')),
  'voice-analytics.index': lazy(() => import('@/lots/voice-analytics/VoiceAnalyticsDashboard')),

  // ODV Brain — living neuro map (drag nodes, draw edges, write directives)
  'brain.BrainMap': lazy(() => import('@/lots/brain/vehicles/BrainMap')),
  'brain.index': lazy(() => import('@/lots/brain/vehicles/BrainMap')),
  'operations.brain': lazy(() => import('@/lots/brain/vehicles/BrainMap')),
};

// Loading state - Valet is fetching your vehicle
const FetchingVehicle = ({ lot, vehicle }) => (
  <div className="flex flex-col items-center justify-center p-8 bg-gray-800 rounded-lg">
    <div className="relative">
      <IconParking className="w-12 h-12 text-gray-600" />
      <IconCar className="w-6 h-6 text-yellow-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
    </div>
    <p className="text-gray-400 mt-4">Valet fetching {vehicle}</p>
    <p className="text-xs text-gray-500">from {lot} lot</p>
  </div>
);

// Error state - Vehicle not found or damaged
const VehicleNotFound = ({ lot, vehicle, error, retry }) => (
  <div className="flex flex-col items-center justify-center p-8 bg-red-900/20 rounded-lg border border-red-500">
    <IconAlertTriangle className="w-12 h-12 text-red-400 mb-4" />
    <p className="text-red-400 text-lg mb-2">Vehicle Not Available</p>
    <p className="text-gray-400 mb-2">
      The valet couldn't fetch "{vehicle}" from the "{lot}" lot.
    </p>
    {error && (
      <details className="text-xs text-red-300 mb-4">
        <summary className="cursor-pointer">Error details</summary>
        <pre className="mt-2 p-2 bg-red-900/30 rounded">{error.message}</pre>
      </details>
    )}
    {retry && (
      <button
        onClick={retry}
        className="px-4 py-2 bg-yellow-400 text-black rounded hover:bg-yellow-500 transition-colors"
      >
        Call Another Valet
      </button>
    )}
  </div>
);

// Error boundary for crashed vehicles
class VehicleSafetyCheck extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasCrashed: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasCrashed: true, error };
  }

  componentDidCatch(error, errorInfo) {
    log.error?.('Vehicle crashed', {
      lot: this.props.lot,
      vehicle: this.props.vehicle,
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasCrashed) {
      return (
        <VehicleNotFound
          lot={this.props.lot}
          vehicle={this.props.vehicle}
          error={this.state.error}
          retry={() => {
            this.setState({ hasCrashed: false, error: null });
            window.location.reload();
          }}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Valet Component - Fetches vehicles from parking lots
 * 
 * @param {string} lot - The parking lot name (e.g., "assignments")
 * @param {string} vehicle - The vehicle name (e.g., "AssignmentCenter")
 * @param {object} props - Props to pass to the vehicle component
 * @param {function} onArrive - Callback when vehicle arrives (component loads)
 * @param {function} onError - Callback when valet can't fetch the vehicle
 */
const Valet = ({ lot, vehicle, props = {}, onArrive, onError }) => {
  // Build the parking spot identifier
  const parkingSpot = `${lot}.${vehicle}`;
  const Vehicle = vehicleRegistry[parkingSpot];

  // Success - vehicle is being fetched (hook must be called before any returns)
  useEffect(() => {
    if (Vehicle && onArrive) {
      onArrive({ lot, vehicle });
    }
  }, [Vehicle, lot, vehicle, onArrive]);

  // Vehicle not in registry
  if (!Vehicle) {
    const notFoundError = new Error(`Vehicle "${vehicle}" not found in "${lot}" lot`);
    if (onError) onError(notFoundError);

    return (
      <VehicleNotFound
        lot={lot}
        vehicle={vehicle}
        error={notFoundError}
      />
    );
  }

  return (
    <VehicleSafetyCheck lot={lot} vehicle={vehicle}>
      <Suspense fallback={<FetchingVehicle lot={lot} vehicle={vehicle} />}>
        <Vehicle {...props} />
      </Suspense>
    </VehicleSafetyCheck>
  );
};

/**
 * Hook for programmatic vehicle fetching
 * 
 * @example
 * const { Vehicle, loading, error } = useValet('assignments', 'AssignmentCenter');
 */
export const useValet = (lot, vehicle) => {
  const [Vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const parkingSpot = `${lot}.${vehicle}`;
    const LazyVehicle = vehicleRegistry[parkingSpot];

    if (!LazyVehicle) {
      setError(new Error(`Vehicle "${vehicle}" not found in "${lot}" lot`));
      setLoading(false);
      return;
    }

    // Vehicle found in registry
    setVehicle(() => LazyVehicle);
    setLoading(false);
    setError(null);
  }, [lot, vehicle]);

  return { Vehicle, loading, error };
};

/**
 * Fetch a vehicle component for use in other components
 * 
 * @param {string} lot - The lot name
 * @param {string} vehicle - The vehicle name
 * @returns {React.Component} The lazy-loaded vehicle component
 */
export const fetchVehicle = (lot, vehicle) => {
  const parkingSpot = `${lot}.${vehicle}`;
  log.debug?.('fetchVehicle called', { lot, vehicle, parkingSpot });

  const LazyVehicle = vehicleRegistry[parkingSpot];
  log.debug?.('fetchVehicle result', { found: !!LazyVehicle });

  if (!LazyVehicle) {
    log.debug?.('Available vehicles for lot', {
      lot,
      vehicles: Object.keys(vehicleRegistry).filter(key => key.startsWith(`${lot}.`))
    });
    throw new Error(`Vehicle "${vehicle}" not found in "${lot}" lot`);
  }

  return LazyVehicle;
};

/**
 * Get list of all available vehicles in a lot
 * 
 * @param {string} lot - The lot name
 * @returns {string[]} List of vehicle names
 */
export const getLotVehicles = (lot) => {
  return Object.keys(vehicleRegistry)
    .filter(spot => spot.startsWith(`${lot}.`))
    .map(spot => spot.replace(`${lot}.`, ''));
};

/**
 * Get list of all lots
 * 
 * @returns {string[]} List of lot names
 */
export const getAllLots = () => {
  const lots = new Set();
  Object.keys(vehicleRegistry).forEach(spot => {
    const [lot] = spot.split('.');
    lots.add(lot);
  });
  return Array.from(lots);
};

/**
 * Check if a vehicle exists in a lot
 * 
 * @param {string} lot - The lot name
 * @param {string} vehicle - The vehicle name
 * @returns {boolean} Whether the vehicle exists
 */
export const hasVehicle = (lot, vehicle) => {
  const parkingSpot = `${lot}.${vehicle}`;
  return parkingSpot in vehicleRegistry;
};

// Export the registry for debugging/inspection
export const inspectRegistry = () => vehicleRegistry;

// Backward compatibility (deprecated)
export const getGarageVehicles = getLotVehicles; // @deprecated Use getLotVehicles
export const getAllGarages = getAllLots; // @deprecated Use getAllLots

export default Valet;
